import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'

export type UseBusinessAreaFilterResult = {
  /**
   * `targetAreaCodes` ∩ 業務エリア設定。 未確定 (取得中、または retry を
   * 使い切った恒久的な失敗) の間は `targetAreaCodes` をそのまま返す
   * (下記 why not 参照)。
   *
   * **契約: 中身が変わらない限り参照も変わらない。** `useMemo` の依存が
   * `personalSettingsData` (React Query の構造的共有により内容が同じ応答では
   * 参照が変わらない) と `targetAreaCodes` (呼び出し元の `DEMAND_AREA_CODES` /
   * `AREA_CODE_OPTIONS` はモジュールレベル定数) のみのため、この保証は
   * 呼び出し元の実装に依存せず本 hook 自身が満たす (review 指摘 A-3:
   * 下流の `useAreaSelection` はこの保証を前提にせず内部で独自に安定化するため
   * 二重の安全網になるが、契約として明記しておくことで将来 `targetAreaCodes`
   * を呼び出し元が毎レンダー新しい配列で渡すよう変更した場合に、この JSDoc が
   * 「呼び出し元は `useMemo` 等で安定した配列を渡す責務を持つ」ことを示す)。
   */
  selectableAreaCodes: readonly AreaCode[]
  /**
   * 業務エリア取得の失敗 (retry 使い切り後の恒久的な失敗を含む)。
   * `selectableAreaCodes` のフォールバック条件には含めない — 呼び出し元が
   * 既存の取得失敗バナーへ合流させて失敗を可視化する想定の値。
   */
  isBusinessAreasError: boolean
  /**
   * 業務エリア設定の取得が「解決済み」 (成功または恒久的な失敗) か。
   * 取得中の間だけ false (= `personalSettingsData === undefined &&
   * !isBusinessAreasError` と同値)。
   *
   * `selectableAreaCodes` は取得中も fail-open で `targetAreaCodes` を返すため、
   * 「値が確定したか」を呼び出し元が `selectableAreaCodes` 自体からは判別できない
   * (submission-management-dashboard-skeleton review 指摘: 呼び出し側が
   * `useGetPersonalSettingsQuery` を二重に呼んで自前判定するのを防ぐため追加。
   * additive な field のため既存 caller (`BalanceDemand` / `BalanceGeneration`)
   * の挙動は変えない)。
   */
  isBusinessAreasResolved: boolean
  /**
   * 業務エリア設定を一度も取得できないまま失敗した (retry 使い切り後)。
   * 取得済みデータがある後の再取得失敗では false (`isBusinessAreasError` は
   * true のまま)。`selectableAreaCodes` の fail-open は変えない。
   * 提出管理 (AC-PSM-075) が空状態案内と区別して読込エラーを出すための additive
   * な値で、`BalanceDemand` / `BalanceGeneration` は読まない。
   */
  isBusinessAreasLoadFailed: boolean
}

/**
 * 対象エリア一覧 (画面ごとの `DEMAND_AREA_CODES` / `AREA_CODE_OPTIONS` 等) を
 * 業務エリア設定で絞り込む共有ポリシー (issue #7535、conventions.md「業務エリア
 * フィルタの原則」)。`BalanceDemand`/`BalanceGeneration` の両ページに複製されて
 * いた「取得・フォールバック・エラー公開」を1箇所に統合する (review 指摘:
 * 複製されたポリシーは、どちらかの画面だけ更新すると業務エリア設定に対する
 * 表示・エラー挙動が画面間で乖離する)。
 *
 * `throwOnError: false`: 呼び出し元の画面は取得失敗しても全画面エラーにしない
 * 方針 (AC-BDB-013 / AC-BDH-013 が画面内バナーを要求)。既定の
 * `throwOnError: true` のままだと、業務エリア取得の失敗が画面ごと落とす依存に
 * なってしまう。
 *
 * 「未確定 (取得中)」の判定は `personalSettingsData === undefined` の 1 本で
 * 行う。retry を使い切って恒久的に失敗した場合も data は undefined のままな
 * ので、これだけでは「取得中」と「失敗」を区別できない。`isBusinessAreasError`
 * を別途返すことで、呼び出し元がこの2つを区別して扱えるようにする。
 *
 * - **why not (失敗時に選択肢を空にする)**: 採らない — 空にすると画面その
 *   ものが操作不能になり、フォールバック (対象エリア全件) の意義 (一時的な
 *   取得不具合でも同時同量監視を止めない) が失われる。
 * - **why not (再取得失敗時に全エリアへ戻す)**: 採らない — 一度取得できた
 *   業務エリアがあるのに全エリアへ戻すと、担当外エリアを表示することになり
 *   AC-BDB-011 / AC-BDH-004・conventions.md「選択肢自体から業務エリア外を
 *   除外する」に反する。`isBusinessAreasError` はフォールバック条件に含め
 *   ない — 判定は常に `personalSettingsData === undefined` のままにする
 *   (取得済みの業務エリアが再取得の一時失敗で失われない)。
 * - **why not (専用のエラー文言・UI を作る)**: 採らない — spec
 *   (AC-BDB-011/014) に無い新しい observable behavior を frontend 側で持ち
 *   込むことになるため。`isBusinessAreasError`
 *   は呼び出し元の既存の取得失敗バナーの発火条件へ合流させるだけに留める
 *   (合流は呼び出し元の責務、本 hook はエラー状態を公開するだけ)。
 */
export function useBusinessAreaFilter(
  targetAreaCodes: readonly AreaCode[],
): UseBusinessAreaFilterResult {
  const { data: personalSettingsData, isError: isBusinessAreasError } =
    useGetPersonalSettingsQuery({ throwOnError: false })

  const selectableAreaCodes = useMemo(() => {
    if (personalSettingsData === undefined) return targetAreaCodes
    const businessAreas = personalSettingsData.settings?.businessAreas ?? []
    return targetAreaCodes.filter((area) => businessAreas.includes(area))
  }, [personalSettingsData, targetAreaCodes])

  const isBusinessAreasResolved =
    personalSettingsData !== undefined || isBusinessAreasError

  const isBusinessAreasLoadFailed =
    personalSettingsData === undefined && isBusinessAreasError

  return {
    selectableAreaCodes,
    isBusinessAreasError,
    isBusinessAreasResolved,
    isBusinessAreasLoadFailed,
  }
}
